import {
  ApplicationRef,
  ChangeDetectorRef,
  Component, ElementRef, EnvironmentInjector, inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  NetscopeApiService,
  Success,
  Response
} from "@app/services/netscope-api.service";
import {
  ActivatedRoute,
  NavigationEnd, NavigationStart,
  Router
} from "@angular/router";
import {
  take
} from "rxjs/operators";
import * as d3 from "d3";
import {
  Location
} from '@angular/common';

import {
  DependenciesViewerRenderer
} from "@app/netscope/netscope-common-flowview/renderers/DependenciesViewerRenderer";
import {
  ProtocolAnalysisRenderer
} from "@app/netscope/netscope-common-flowview/renderers/ProtocolAnalysisRenderer";
import {VisualisationDataForRenderers
} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRenderer";
import {
  AppsGroupsRendererJointJs
} from "@app/netscope/netscope-common-flowview/renderers/AppsGroupsRendererJointJs";
import {
  filter
} from 'rxjs/operators';

import {combineLatest, ReplaySubject, Subscription} from "rxjs";

import {ShepherdService} from 'angular-shepherd';
import {generateFlowComponentShepperdParameters} from "@app/netscope/netscope-common-flowview/data/shepherd-data";
import {TranslocoService} from "@ngneat/transloco";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {ComboboxSelectionItem, GraphDataObject, ComputingResourceCollection, VirtualMachineWithAppsGroups, TemporalParameter, HostSystem, VirtualMachine, DCNetscopeResourceWithName, FlowDetail, FlowRessourceObject, AppsGroups, DuckDbDashboardData, AppsGroupMembersDisplayMode, MemberRoles, memberIsVisibleInMode, IncludedAppsGroupBoxState, defaultDisplayMode, AppsGroupsData, ResolvedAppsGroupsInclusion} from "@app/netscope/model/data-models";
import {NetworksViewerRenderer} from "@app/netscope/netscope-common-flowview/renderers/NetworksViewerRenderer";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {EChartsOption} from "echarts";
import {
  ClustersViewerRendererSigmaJS
} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRendererSigmaJS";
import {
  ClustersViewerRendererCosmosGL
} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRendererCosmosGL";
import {
    NetscopeCommonFlowviewLegendComponent
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview-legend/netscope-common-flowview-legend.component";
import {FlowsRepository, ProtocolAnalysisSelection} from "@app/netscope/model/flows-repository";
import {
  AppsGroupsPanelComponent
} from "@app/netscope/netscope-common-flowview/apps-groups-panel/apps-groups-panel.component";
import {environment} from "../../../environments/environment";
import {AppsGroupsLinksMode} from "@app/netscope/netscope-common-flowview/misc/FlowsDescriptionsClassification";
import {FlowViewHost} from "@app/netscope/netscope-common-flowview/flowview-host";
import {
  resolveIncludedAppsGroups,
  setUuidForAppsGroups
} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsLoading";


export interface FlowsRendererInterface {
  // The renderers drawing asynchronously give back when they are done
  render(flowComponent: FlowViewHost): void | Promise<void>;
  destroy(flowComponent: FlowViewHost): void;

  /**
   * Writes what the renderer drew to a file. Only the renderers drawing on a canvas have one: the ones
   * drawing with d3 leave their SVG behind in 'flowComponent.svg', which the view exports itself.
   */
  exportAsImage?(): void;

  // Writes what the renderer drew to a file of the given format, for the renderers offering several of them
  exportAs?(format: string): void;

  zoomInVisualisation(flowComponent: FlowViewHost): void;
  zoomOutVisualisation(flowComponent: FlowViewHost): void;
  recenterVisualisation(flowComponent: FlowViewHost): void;

  update(flowComponent: FlowViewHost): void;
  isOutdated(): boolean;
}

export class AppGroupState {
  appGroup: AppsGroups;
  mode: AppsGroupMembersDisplayMode;
  // Whether the flows between the members of the group are drawn. They are hidden by default: a group usually
  // talks to itself a lot, and those links would bury the flows it exchanges with the other groups.
  showInternalFlows: boolean = false;
  // Apps groups included in this one that the user opened, keyed by 'file/name' (see includedAppsGroupKey).
  // An included group is drawn as a single box inside this one until it is opened, its members then showing
  // inside that box.
  expandedIncludedAppsGroups: Set<string> = new Set();
  // Apps groups included in this one that the user folded into it from the panel, keyed as above. They get no
  // box: their members are drawn as the ones this group lists itself, as a member folded into it or on its own.
  hiddenIncludedAppsGroups: Set<string> = new Set();
  // Whether the group is drawn as a single box, its sub-groups and its members folded into it. Unset until the
  // user folds or unfolds it: the view then decides (see FlowViewHost.appsGroupsFoldedByDefault).
  folded?: boolean;
  uuid: string;
  name: string;
}

/**
 * Display choices made on the apps groups of one apps groups file: which groups are hidden, how the members
 * of each of them are folded, and which members are shown as nodes of their own.
 *
 * They are held per file, as they are keyed by the name of an apps group and by the uuid of a member, both of
 * which another file may hold as well: a member shown under a file would otherwise stay shown under the next
 * one, whatever the folding mode of the apps group holding it there.
 */
export class AppsGroupsViewState {
  appGroupStates: Map<string, AppGroupState> = new Map();
  // Keyed by apps group and member (see memberVisibilityKey)
  vmVisibilityStates: Map<string, boolean> = new Map();
  hiddenAppsGroups: Set<string> = new Set();
}

@Component({
  standalone: false,
  selector: 'app-netscope-common-flowview',
  templateUrl: './netscope-common-flowview.component.html',
  styleUrls: ['./netscope-common-flowview.component.css']
})
export class NetscopeCommonFlowviewComponent implements OnInit, OnDestroy, FlowViewHost {

  protected readonly environment = environment;

  appRef = inject(ApplicationRef);
  injector = inject(EnvironmentInjector);

  isLoading = false;
  openHeatmapModal = false;
  failureMode = false;
  svg;
  height = 1024;
  width = 768;
  chartCheckInterval;
  linksAreColored = "yes";
  showIndirectLinks = "yes";
  skipNextUrlChange = false;
  style = getComputedStyle(document.body);
  isSlow = false;

  lastSvgWidth = 0;
  lastSvgHeight = 0;

  focusedProtocols = [];

  root = [];
  clustersFlows: FlowDetail[] = [];
  flowsRepository: FlowsRepository;
  datatableTabFlowsRepository: FlowsRepository;
  sidePanelRepository: FlowsRepository;
  lastDuckdbData: DuckDbDashboardData;

  filteredLinks: FlowDetail[] = [];
  nodeRadius = 15;
  startTime = 0;
  endTime = 0;
  temporalParameter: TemporalParameter;
  timeResolution = "daily";

  isDarkMode: boolean = false;

  routingAlgorithm: string = "automatic";
  currentRoutingAlgorithm: string = "";

  visualisationTabIsSelected = true;
  datagridTabIsSelected = false;

  @ViewChild('divFlowsSvg')
  public divFlowsSvgEl: ElementRef;

  // @ViewChild('combobox')
  // combobox: ClrCombobox<any>;

  @ViewChild('legend')
  legend: NetscopeCommonFlowviewLegendComponent;

  @ViewChild('dataSettingsPanel')
  dataSettingsPanel;

  @ViewChild('appsGroupsPanel')
  appsGroupsPanel: AppsGroupsPanelComponent;

  graphParameters = {
    userMovedMap: false,
    lastTransformation: undefined,
    firstTransformation: undefined
  }

  updateFlag = false;
  // selectedProtocolNode: EnrichedWithProtocolAnalysisRendererNode;

  gravityValue = undefined; // It will be redefined when we have the numbers of displayed VMs
  gravityMode = "automatic";

  lastForcePositions = {};
  lastAlpha = 0.0;
  simulationAlreadyLoaded = false;
  simulationShouldRestart = false;

  currentSelection: ProtocolAnalysisSelection[] = [];
  focusLevel = 1;
  focusNode;
  zoomFunction;

  lastDataUuid: string;

  perProtocolFlow = [];

  groupsApps: AppsGroups[] = [];
  groupsAppsSubject: ReplaySubject<AppsGroups[]> = new ReplaySubject<AppsGroups[]>(1);
  selectedAppsGroups = [];

  appsGroupsMode: string = "networks";

  // Selected view can be "protocol-analysis", "dependencies-viewer", "clusters-viewer" or "apps-groups"
  selectedView = "dependencies-viewer";
  // Selected view can be "Protocols analysis", "Dependencies viewer", "Clusters Viewer" or "Apps Groups"
  viewLabel = "Dependencies viewer";

  appGroupStates: Map<string, AppGroupState> = new Map();
  // Members of the apps groups shown as nodes of their own. A member that is not in here is folded into the
  // node of its apps group, where its flows are drawn at the level of the group. Keyed by apps group and member
  // (see memberVisibilityKey): a member is shown in the group drawing it, not in every group holding it.
  vmVisibilityStates: Map<string, boolean> = new Map();
  // Role played by every resource in the flows of the displayed period, filled by the apps groups renderer as
  // it is the only place holding the whole graph data. The display modes are presets over these roles.
  memberRoles: Map<string, MemberRoles> = new Map();
  // Apps groups the user chose to hide: they are not rendered at all
  hiddenAppsGroups: Set<string> = new Set();
  // Display choices of the apps groups files other than the displayed one, whose own choices are the three
  // fields above. They are kept, so that coming back to a file finds them as they were left.
  appsGroupsViewStatesByFile: Map<string, AppsGroupsViewState> = new Map();

  isListeningToRouteChange = false;
  onResizeEventSubscription: Subscription;
  navigationEventSubscription: Subscription;
  onSelectedResourcesChangeSubscription: Subscription;
  combinedDataSubjectSubscription: Subscription;

  clusterViewerVisibleResourcesOptions = {
    "infrastructure": true,
    "external_ips": true,
    "unknown_ips": false,
    "internet": false,
  }

  clusterViewerShowASNs = "asns";
  showIgnoredPorts = false;

  reComputingPositionsRequested = false;
  stopComputingPositionsRequested = false;
  isComputingPositions = false;

  // Initialize with active directory / LDAP ports
  ignoredPorts = [389, 445, 139, 138, 137, 636, 53, 123, 88, 464, 135, 9389];
  newPortStr = "";
  draggableNodesEnabled = false;
  showInternalFlowsAppsGroups = false;
  // How the links of the apps groups view take the flows descriptions into account (see AppsGroupsLinksMode)
  appsGroupsLinksMode: AppsGroupsLinksMode = "ignore";

  currentMaxEvent = 0;

  // FLOWS SIDE PANELS
  sidePanelOpened = false;
  sidePanelFlows: FlowDetail[] = [];
  sidePanelTitle: string = "Flow details";
  sidePanelObject: FlowRessourceObject;
  sidePanelResourceUuid: string;
  sidePanelIsAlreadyFocused: boolean;
  sidePanelProtocolsOptions: EChartsOption;
  sidePanelOriginsOptions: EChartsOption;
  sidePanelHideFocus: boolean = false;

  // Floating apps groups panel, shown by default when the apps groups view is displayed
  appsGroupsPanelOpened = true;
  mode: string = "automatic";

  renderer: FlowsRendererInterface;
  rendererVersion = 0;

  showCameraButtons: boolean = false;

  // DATA
  vms: ComputingResourceCollection<VirtualMachine> = new ComputingResourceCollection<VirtualMachine>();
  hosts: ComputingResourceCollection<HostSystem> = new ComputingResourceCollection<HostSystem>();

  currentComboboxSelection: ComboboxSelectionItem[] = [];

  constructor(public netscopeService: NetscopeApiService,
              private activatedRouter: ActivatedRoute, public route: Router,
              private location: Location, private shepherdService: ShepherdService,
              public translocoService: TranslocoService, private netscopeUiService: NetscopeUiService,
              private router: Router,
              private ref: ChangeDetectorRef) {
  }

  ngOnInit(): void {

    if (!this.isListeningToRouteChange) {
      this.isListeningToRouteChange = true;

      this.navigationEventSubscription = this.route.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          // @ts-ignore
          let snapshot = this.activatedRouter.snapshot;
          //Do something with the NavigationEnd event object.
          console.log(event);

          let currentUrl = snapshot['_routerState'].url;

          // Get state from the current router state
          const state = this.router.getCurrentNavigation()?.extras?.state || history.state;

          console.log('Custom data:', state);

          if (state.ignore) {
              console.log("Ignoring event (It was likely just an update of parameters in URL bar)");
              return;
          }

          // Check that the id of the event has not been processed yet
          if (event.id > this.currentMaxEvent && currentUrl === event.url) {
            this.currentMaxEvent = event.id;
            this.processRouterParams(snapshot.params, snapshot.queryParams);
            this.updateSelectedView(false);
          }
        });

      this.processRouterParams(this.activatedRouter.params, this.activatedRouter.queryParams);
    }

    let combinedDataSubject = combineLatest([
      this.netscopeUiService.comboboxSelectionItemSubject,
      // this.netscopeUiService.clustersFlowDetailsSubject,
      this.netscopeUiService.duckDbRefinedDataSubject,
    ]);
    this.combinedDataSubjectSubscription = combinedDataSubject.subscribe(([comboboxSelectionItems, duckDbData]) => {
      // this.clustersFlows = clustersFlows.flows;
      this.flowsRepository = duckDbData.flowsRepository.clone();
      this.currentComboboxSelection = comboboxSelectionItems;
      this.lastDuckdbData = duckDbData;
      // this.selectedProtocolNode = undefined;

      if (this.lastDataUuid !== duckDbData.dataUuidAndWhere) {
        this.lastDataUuid = duckDbData.dataUuidAndWhere;
        this.reloadUi();
      } else {
        console.log(`It seems that data with uuid=${duckDbData.dataUuidAndWhere} has already been processed, I will skip it`);
      }
    });

    this.netscopeUiService.temporalParametersSubject.subscribe((newTemporalParameters) => {
      this.temporalParameter = newTemporalParameters;
    });

    this.route.events.subscribe((event) => {
      if (event instanceof NavigationStart) {

        if (event.url.includes("/flows/dependencies-viewer")) {
          return;
        }
        if (event.url.includes("/flows/protocol-analysis")) {
          return;
        }
        if (event.url.includes("/flows/clusters-viewer")) {
          return;
        }
        if (event.url.includes("/flows/apps-groups")) {
          return;
        }
        if (event.url.includes("/flows/networks-viewer")) {
          return;
        }
        console.log(event);
        this.ngOnDestroy();
      }
    });

    this.onResizeEventSubscription = this.netscopeUiService.onResizeEventEmitter.subscribe(() => {
      if (this.clustersFlows.length > 0) {
        this.reloadUi();
      }
    });

    this.onSelectedResourcesChangeSubscription = this.netscopeUiService.selectedResourcesChangeEmitter.subscribe((newResources) => {
      // When a focus is made (from this component or from a other component), an event is sent to the netscopeUiService
      // which notify the main component (who has the search bar), which in turn ask the DuckDBService to reload its data
      // once it is done, we received a new set of data from duckdb and the 'selectedResourcesChange' is triggered.
      // We wait for this signal here, and ask to redraw the visualisation.
      this.reloadDataAndRecomputeDistances();
    });

    // Skip first dark mode theme change
    let firstLoading = true
    this.netscopeUiService.darkModeStatusSubject.subscribe((darkModeStatus: boolean) => {
      this.isDarkMode = darkModeStatus;
      if (firstLoading) {
        firstLoading = false;
      } else {
        this.reloadUi();
      }
    });

    // this.netscopeUiService.duckDbRefinedDataSubject.subscribe((dashboardData) => {
    //   this.reloadUi();
    // })

    // this.netscopeUiService.checkDarkMode((isDarkMode: boolean) => {
    //    this.isDarkMode = isDarkMode;
    // });
  }

  ngOnDestroy () {
    if (this.onResizeEventSubscription !== undefined) {
      this.onResizeEventSubscription.unsubscribe();
    }
    if (this.navigationEventSubscription !== undefined) {
      this.navigationEventSubscription.unsubscribe();
    }
    if (this.onSelectedResourcesChangeSubscription !== undefined) {
      this.onSelectedResourcesChangeSubscription.unsubscribe();
    }
    if (this.combinedDataSubjectSubscription !== undefined) {
      this.combinedDataSubjectSubscription.unsubscribe();
    }

    if (this.chartCheckInterval) {
      clearInterval(this.chartCheckInterval);
    }

    if (this.renderer) {
      this.renderer.destroy(this);
      this.renderer = undefined;
    }
  }

  askRefreshAngular = () => {
    this.ref.detectChanges();
  }

  launchTour() {
    let flowDatatableButton = document.getElementById("flows_visualisation_button");
    if (flowDatatableButton !== null) {
      flowDatatableButton.click();
    }

    let currentDateAsDatetime = new Date(this.startTime * 1000);
    let currentDateFormatted = this.timeResolution == "daily" ? currentDateAsDatetime.toLocaleDateString() : currentDateAsDatetime.toLocaleString();
    let stepsParameters = generateFlowComponentShepperdParameters(this, this.viewLabel, currentDateFormatted);

    // Close heatmap
    this.openHeatmapModal = false;
    // Prepare shepard tour component
    this.shepherdService.defaultStepOptions = stepsParameters.defaultStepOptions;
    this.shepherdService.modal = true;
    this.shepherdService.confirmCancel = false;
    this.shepherdService.addSteps(stepsParameters.steps);
    this.shepherdService.start();
    this.shepherdService.tourObject.on('complete', (event) => {
      this.openHeatmapModal = false;
      let flowDatatableButton = document.getElementById("flows_visualisation_button");
      if (flowDatatableButton !== null) {
        flowDatatableButton.click();
      }
    });
    this.shepherdService.tourObject.on('cancel', (event) => {
      this.openHeatmapModal = false;
      let flowDatatableButton = document.getElementById("flows_visualisation_button");
      if (flowDatatableButton !== null) {
        flowDatatableButton.click();
      }
    });
  }

  fixedGetParams = (queryParams, paramName) => {
    if (paramName in queryParams) {
      return queryParams[paramName];
    }

    if ("_value" in queryParams) {
      return queryParams._value[paramName];
    }

    return undefined;
  }

  processRouterParams = async (params, queryParams) => {
    // Check if a view is specified in the parameters
    let selectedView: string = this.fixedGetParams(params, "selected_view");

    // Remove arguments from selectedView: they are generated by updateUrl() function.
    if (selectedView !== undefined && selectedView.indexOf("?") !== -1) {
      selectedView = selectedView.split("?")[0];
    }

    if (selectedView !== undefined) {
      this.selectedView = selectedView;
      this.updateViewLabel();
    }

    // Take into account protocols
    let protocols = this.fixedGetParams(params, "protocols");
    if (protocols !== undefined) {
      this.focusedProtocols = [protocols]
    }

    // Load apps groups
    if (this.groupsApps === undefined || this.groupsApps.length === 0) {
        // let useAutomaticAppsGroups = this.appsGroupsMode == "automatic";
        let appGroupMode: string = this.appsGroupsMode;
        let appsGroupsPromise = this.netscopeService
            .getAppsGroups(appGroupMode)
            .pipe(take(1))
            .toPromise();
      let all_apps_groups_response: Response<AppsGroupsData> = await appsGroupsPromise;

      if (all_apps_groups_response instanceof Success) {
        let success_all_apps_groups_response: Success<AppsGroupsData> = all_apps_groups_response
        this.groupsApps = success_all_apps_groups_response.element.apps_groups;
        this._setUuidForAppsGroups();
        await this._resolveIncludedAppsGroups();
        // Reuse previous uuids if they exist
        for (let appGroup of this.groupsApps) {
          if (this.appGroupStates.has(appGroup.name)) {
            let state = this.appGroupStates.get(appGroup.name);
            appGroup.mode = state.mode;
          }
        }
        this.groupsAppsSubject.next(this.groupsApps);
      }
    }
  }

  addNewIgnoredPort = () => {
    try {
      let newPortAsInt = Number.parseInt(this.newPortStr);
      if (this.ignoredPorts.indexOf(newPortAsInt) == -1) {
        this.ignoredPorts.push(newPortAsInt);
        this.reloadUiAndRecomputeDistances();
      }
      this.newPortStr = "";
    } catch (e) {
      console.warn(`could not add new port : invalid number (${this.newPortStr}). More details below :`);
      console.warn(e)
    }
  }

  removeIgnoredPort = (port) => {
    this.ignoredPorts = this.ignoredPorts.filter((existingPort) => existingPort != port);
    this.reloadUiAndRecomputeDistances();
  }

  updateFocusedVmsFromSelectedAppsGroups = () => {
    let vmsIndex = new Map<string, VirtualMachineWithAppsGroups>();
    let selectedGroupsAppsNames = this.selectedAppsGroups.map((g) => g.name);
    for (let appGroup of this.groupsApps) {

      if (selectedGroupsAppsNames.indexOf(appGroup.name) === -1) {
        continue;
      }

      for (let vm of appGroup.vms) {
        if (vmsIndex[vm.uuid] === undefined) {
          // TODO: check for XenOrchestra support
          let vmUuid = `vim.VirtualMachine:${vm.uuid}`
          vmsIndex[vm.uuid] = new VirtualMachineWithAppsGroups(vmUuid, undefined, []);
        }
        vmsIndex[vm.uuid].apps_groups.push(appGroup.name);
      }
    }

    let existingVmsInFocusUuid = this.currentComboboxSelection
      .filter((s) => s.mode === "focus")
      .map((s) => s.resource.uuid);

    Object.values(vmsIndex).map((vmObject) => {
      if (existingVmsInFocusUuid.indexOf(vmObject.uuid) === -1) {
        this.currentComboboxSelection.push(new ComboboxSelectionItem(new DCNetscopeResourceWithName(vmObject.uuid, undefined), "focus"));
      }
    });
  }

  reloadUrlParametersDataAndRecomputeDistances = () => {
    this.simulationAlreadyLoaded = false;
    // this.reloadData();
  }

  reloadDataAndRecomputeDistances = () => {
    this.simulationAlreadyLoaded = false;
    // this.reloadData();
  }

  reloadUrlParametersUiAndRecomputeDistances = () => {
    this.simulationShouldRestart = true;
    this.reloadUi();
  }

  onAppsGroupsSelection = async () => {

      let appGroupsPromise = this.netscopeService
        .getAppsGroups(this.appsGroupsMode)
        .pipe(take(1))
        .toPromise();

    let all_apps_groups_response: Response<AppsGroupsData> = await appGroupsPromise;

    if (all_apps_groups_response instanceof Success) {
      let success_all_apps_groups_response: Success<AppsGroupsData> = all_apps_groups_response
      this.groupsApps = success_all_apps_groups_response.element.apps_groups;
      this._setUuidForAppsGroups();
      await this._resolveIncludedAppsGroups();
      this.groupsAppsSubject.next(this.groupsApps);
    }

    this.reloadUiAndRecomputeDistances();
  }

  reloadUiAndRecomputeDistances = () => {
    this.simulationShouldRestart = true;
    this.reloadUi();
  }

  reloadUi = () => {
    // Refresh network view
    this.eraseNetworkView();
    this.createNetworkView();
  }

  switchFocus = (vm, mode="focus") => {
    this.sidePanelOpened = false;
    this.netscopeUiService.switchFocus(vm, mode);
  }

  switchIgnore = (vm) => {
    this.sidePanelOpened = false;
    this.netscopeUiService.switchFocus(vm, "ignore");
    this.sidePanelOpened=false;
  }

  updateLegend(visualisationData: VisualisationDataForRenderers) {
    let intervalSetLegend = setInterval(() => {
        if (this.legend === undefined) {
            console.log("Legend is not ready, I will try again later");
            return true
        }
        this.legend.updateLegend(this.selectedView, visualisationData);
        clearInterval(intervalSetLegend);
        return true;
    }, 50);
  }

  /**
   * This method export the config table into a CSV file
   */
  exportCSV = () => {
    let csvHeader = "Source, Destination, SourceHost, DestinationHost, ExchangedBytes, TotalPackets"
    let csvContent = csvHeader + "\n";
    for (let link of this.filteredLinks) {
      let sourceName = link.source.name;
      let targetName = link.target.name;
      let sourceHostName = link.source.host === undefined ? "na" : link.source.host.name;
      let targetHostName = link.target.host === undefined ? "na" : link.target.host.name;
      let lineValue =
        `${sourceName}, ${targetName}, ${sourceHostName}, ${targetHostName}, ${link.exchanged_bytes}, ${link.exchanged_packets}\n`;
      csvContent += lineValue;
    }

    let exportedFilename = 'netscope-dependencies.csv';
    let blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    // @ts-ignore
    if (navigator.msSaveBlob) { // IE 10+
      // @ts-ignore
      navigator.msSaveBlob(blob, exportedFilename);
    } else {
      let link = document.createElement("a");
      if (link.download !== undefined) { // feature detection
        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", exportedFilename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  exportRowPerPortCSV = () => {
    let csvHeader = "Source, Destination, Port, Protocol, ExchangedBytes"
    let csvContent = csvHeader + "\n";
    for (let flow of this.perProtocolFlow) {
      let lineValue =
        `${flow.source.name}, ${flow.target.name}, ${flow.port}, ${flow.protocol}, ${flow.exchanged_bytes}\n`;
      csvContent += lineValue;
    }

    let exportedFilename = 'netscope-protocols.csv';
    let blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    if (navigator.msSaveBlob) { // IE 10+
      navigator.msSaveBlob(blob, exportedFilename);
    } else {
      let link = document.createElement("a");
      if (link.download !== undefined) { // feature detection
        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", exportedFilename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  // Exports offered by the apps groups view, which draws its files from its own model (see AppsGroupsGraphExport)
  exportGraphAs = (format: "svg" | "png" | "drawio") => {
    if (this.renderer?.exportAs != undefined) {
      this.renderer.exportAs(format);
    } else {
      this.exportGraph();
    }
  }

  exportGraph = () => {
    // A renderer drawing on a canvas is the only one able to read its own picture back
    if (this.renderer?.exportAsImage != undefined) {
      this.renderer.exportAsImage();
    } else if (this.svg == undefined) {
      // The renderers that do not draw with d3 leave no SVG behind: there is nothing to write out
      console.warn(`[${this.selectedView}] this view cannot be exported as a graph`);
    } else {
      let svgBody = this.svg.html();
      let svgCode =
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">${svgBody}</svg>`;
      let exportedFilename = 'netscope-dependencies.svg';

      let blob = new Blob([svgCode], {
        type: 'text/csv;charset=utf-8;'
      });
      // @ts-ignore
      if (navigator.msSaveBlob) { // IE 10+
        // @ts-ignore
        navigator.msSaveBlob(blob, exportedFilename);
      } else {
        let link = document.createElement("a");
        if (link.download !== undefined) { // feature detection
          // Browsers that support HTML5 download attribute
          let url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", exportedFilename);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  }

  updateViewLabel = () => {
    let viewLabel = "unknown";
    let iconShape = "unknown";
    if (this.selectedView === "protocol-analysis") {
      this.viewLabel = "Protocol analysis";
      viewLabel = "protocol_analysis_name";
      iconShape = "chat-bubble";
    }
    if (this.selectedView === "dependencies-viewer") {
      this.viewLabel = "Dependencies viewer";
      viewLabel = "dependency_viewer_name";
      iconShape = "organization";
    }
    if (this.selectedView === "clusters-viewer") {
      this.viewLabel = "Clusters viewer";
      viewLabel = "clusters_viewer_name";
      iconShape = "recycle";
    }
    if (this.selectedView === "apps-groups") {
      this.viewLabel = "Apps Groups";
      viewLabel = "apps_groups_name";
      iconShape = "node-group";
    }
    if (this.selectedView === "networks-viewer") {
      this.viewLabel = "Networks viewer";
      viewLabel = "networks_viewer_name";
      iconShape = "network-switch";
    }

    this.netscopeUiService.setViewLabel(viewLabel);
    this.netscopeUiService.setIconShape(iconShape);
  }

  createNetworkView(): void {
    d3.select('div#divFlowsSvg').select("svg").remove();

    // The SVG of the view being left is gone: a renderer drawing with d3 sets a new one as it renders,
    // and the others leave it undefined rather than letting the former graph be exported as theirs
    this.svg = undefined;

    console.log(`I will render flows with ${this.selectedView} renderer (15h32)`);

    this.updateViewLabel();

    // Reset slow rendering warning
    this.isSlow = false;

    console.log("debug1");

    this.showCameraButtons = false;

    // Clean previous renderer
    if (this.renderer) {
      this.renderer.destroy(this);
    }

    if (this.selectedView === "protocol-analysis") {
      this.showCameraButtons = true;
      this.renderer = new ProtocolAnalysisRenderer();
    }
    if (this.selectedView === "dependencies-viewer") {
      this.showCameraButtons = true;
      this.renderer = new DependenciesViewerRenderer();
    }
    if (this.selectedView === "clusters-viewer") {
      this.showCameraButtons = true;
      // this.renderer = new ClustersViewerRendererV2();
      // this.renderer = new ClustersViewerRenderer();
      // this.renderer = new ClustersViewerRendererGraphGL();
      // POC: GPU layout + rendering with cosmos.gl, swap the two lines below to try it out.
      this.renderer = new ClustersViewerRendererCosmosGL();
      // this.renderer = new ClustersViewerRendererSigmaJS();
    }
    if (this.selectedView === "networks-viewer") {
      this.showCameraButtons = true;
      this.renderer = new NetworksViewerRenderer();
    }
    if (this.selectedView === "apps-groups") {
      this.showCameraButtons = true;
      // this.renderer = new AppsGroupsRenderer();
      let renderer = new AppsGroupsRendererJointJs();
      // The flows already loaded are drawn again rather than loaded anew, when they are still the ones to draw
      renderer.takeLoadedDataFrom(this.renderer);
      this.renderer = renderer;
    }

    if (this.selectedView === "apps-groups") {
      this.groupsAppsSubject.pipe(take(1)).subscribe((appGroup) => {
        this.renderer.render(this);
      })
    } else {
      if (this.renderer !== undefined) {
        this.renderer.render(this);
      }
    }
    console.log("debug2");
  }

  eraseNetworkView(): void {
    d3.select('div#divFlowsSvg')
      .selectAll('*')
      .remove();
  }

  removeSelection = () => {
    // if (this.selectedView !== "apps-groups") {
    //   this.groupsApps = [];
    // }
    // this.selectedAppsGroups = [];
    // this.currentComboboxSelection = [];
    // // this.reloadUi();
    // this.updateUrl();
    // this.reloadUiAndRecomputeDistances();
  }

  navigateSelection = (where) => {
    this.selectedView = where;
    this.skipNextUrlChange = true;
    // this.updateSelectedView(true);
    this.reloadUi();
  }

  resetVisualisationCommonVariables = () => {
    this.renderer = undefined;
    this.graphParameters = {
      userMovedMap: false,
      lastTransformation: undefined,
      firstTransformation: undefined
    }

    this.updateFlag = false;

    this.gravityValue = undefined; // It will be redefined when we have the numbers of displayed VMs
    this.gravityMode = "automatic";

    this.lastForcePositions = {};
    this.lastAlpha = 0.0;
    this.simulationAlreadyLoaded = true;
    this.simulationShouldRestart = true;
  }

  updateSelectedView = (updateUrl = true) => {
    if (this.selectedAppsGroups.length > 0) {
      this.updateFocusedVmsFromSelectedAppsGroups();
    }
    this.resetVisualisationCommonVariables();
    this.reloadUi();
  }

  /*
##########################################
#
# DEPENDENCIES VIEWER
#
##########################################
 */

  selectProtocolNode = (d: ProtocolAnalysisSelection) => {
    // this.selectedProtocolNode = d;
    let indexGivenNode = this.currentSelection.indexOf(d);

    let newSelection: ProtocolAnalysisSelection[] = [];
    for (let item of this.currentSelection) {
      newSelection.push(item);
      if (item == d) {
        break;
      }
    }
    this.currentSelection = newSelection;

    this.reloadUi();
  }

  zoomInVisualisation = () => {
    this.renderer.zoomInVisualisation(this);
  }

  zoomOutVisualisation = () => {
    this.renderer.zoomOutVisualisation(this);
  }

  recenterVisualisation = () => {
    this.renderer.recenterVisualisation(this);
  }

  askShowSidePanel = (title: string, where: string, resourceId: string=undefined, sidePanelObject:FlowRessourceObject=undefined) => {
    this.sidePanelRepository = this.flowsRepository.clone();
    this.sidePanelRepository.setWhereClause(where);
    this.sidePanelRepository.aggregated_flows_files = this.sidePanelRepository.flows_files;
    this.sidePanelRepository.showFirstPage();
    this.sidePanelRepository.getFlowsMetricsWhere(where).then((flowsDetails: FlowDetail[]) => {
      this.showSidePanel(title, flowsDetails, resourceId, sidePanelObject);

      this.askRefreshAngular();
    })
  }

  showSidePanel = (title: string, flowsDetails: FlowDetail[], resourceId: string=undefined, sidePanelObject:FlowRessourceObject=undefined) => {
    this.sidePanelTitle = title;
    this.sidePanelObject = sidePanelObject;

    let flowDetailsToBeUsed: FlowDetail[] = [];
    for (let flowDetail of flowsDetails) {
      if (flowDetail.subflows.length == 0) {
        flowDetailsToBeUsed.push(flowDetail);
      } else {
        flowDetailsToBeUsed.push(...flowDetail.subflows);
      }
    }

    // let sidePanelFlows = NetscopeUtils.flowsDetailsToPerPortFlowDetails(flowDetailsToBeUsed);
    let sidePanelFlows = flowDetailsToBeUsed;
    this.sidePanelFlows = sidePanelFlows;
    this.sidePanelResourceUuid = resourceId;

    if (this.sidePanelResourceUuid !== undefined && this.sidePanelResourceUuid.includes("vim.asn")) {
      this.sidePanelHideFocus = true;
    } else {
      this.sidePanelHideFocus = false;
    }

    let currentFocusUuids = this.currentComboboxSelection
      .filter((s) => s.mode === "focus")
      .map((s) => s.resource.uuid);

    this.sidePanelIsAlreadyFocused = currentFocusUuids.indexOf(resourceId) !== -1;

    let insideFlows: FlowDetail[] = [];
    let outsideFlows: FlowDetail[] = [];

    for (let flowDetail of flowDetailsToBeUsed) {
      let isInsideFlow = true

      if ((flowDetail.source.type == "internet_ip") || (flowDetail.target.type == "internet_ip")) {
        isInsideFlow = false;
      }
      if (flowDetail.source.type === "asn" || flowDetail.target.type === "asn" ) {
        isInsideFlow = false;
      }

      if (isInsideFlow) {
        insideFlows.push(flowDetail);
      } else {
        outsideFlows.push(flowDetail);
      }
    }

    let insideFlowsExchangedBytes = insideFlows
      .map((flow) => flow.exchanged_bytes)
      .reduce((a, b) => a + b, 0);

    let outsideFlowsExchangedBytes = outsideFlows
      .map((flow) => flow.exchanged_bytes)
      .reduce((a, b) => a + b, 0);

    let originsData = [{
      name: "inside",
      value: insideFlowsExchangedBytes,
    }, {
      name: "outside",
      value: outsideFlowsExchangedBytes,
    }];

    let protocolsExchangedBytes: Map<string, number> = new Map<string, number>();
    for (let flowDetail of flowDetailsToBeUsed) {
      for (let metric of flowDetail.metrics.values()) {
        if (!protocolsExchangedBytes.has(metric.application_protocol)) {
          protocolsExchangedBytes.set(metric.application_protocol, 0);
        }
        protocolsExchangedBytes.set(metric.application_protocol, protocolsExchangedBytes.get(metric.application_protocol) + metric.exchanged_bytes);
      }
    }
    let protocolsData: {name: string, value: number}[] = [];
    for (let [protocol, exchangedBytes] of Array.from(protocolsExchangedBytes.entries())) {
      protocolsData.push({
        "name": protocol,
        "value": exchangedBytes
      });
    }

    // TODO: replace the following hard-coded value with something from css variables, like <lightModeTextColor>
    let darkModeTextColor = "white";
    let lightModeTextColor = "#333";

    let textColor = this.isDarkMode ? darkModeTextColor : lightModeTextColor;

    // Update pie charts
    this.sidePanelProtocolsOptions = {
      title: {
        text: "Protocols",
        textStyle: {
          color: textColor
        }
      },
      // legend: {},
      // height: 300,
      tooltip: {
        formatter: function (params) {
          return `
            ${params.marker} ${params.name}: ${NetscopeUtils.formatBytes(params.value)} (${params.percent}%)
          `;
        }
      },
      series: [{
        type: 'pie',
        data: protocolsData,
        label: {
          color: textColor,
          textBorderWidth: 0,
        }
      }],
    };

    this.sidePanelOriginsOptions = {
      title: {
        text: "Origin",
        textStyle: {
          color: textColor
        }
      },
      // legend: {},
      type: "pie",
      // height: 300,
      tooltip: {
        formatter: function (params) {
          return `
            ${params.marker} ${params.name}: ${NetscopeUtils.formatBytes(params.value)} (${params.percent}%)
          `;
        }
      },
      series: [{
        type: 'pie',
        data: originsData,
        label: {
          color: textColor,
          textBorderWidth: 0,
        }
      }],
    };

    this.sidePanelOpened = true;
  }

  // See resolveIncludedAppsGroups
  _resolveIncludedAppsGroups = async () => {
    await resolveIncludedAppsGroups(this.netscopeService, this.appsGroupsMode, this.groupsApps);
  }

  _setUuidForAppsGroups = () => {
    setUuidForAppsGroups(this.groupsApps);
  }

  focusedOnVisualizationTab = () => {
    // if (this.renderer != undefined && this.renderer.isOutdated()) {
      console.log("Detected outdated renderer, I will reload the UI");
      this.reloadUi();
    // }
  }

  focusedOnDatagridTab = () => {
  }

  // Called when the user picks another apps groups file in the floating panel
  onAppsGroupsFileChanged = (appsGroupsFile: string) => {
    if (appsGroupsFile == this.appsGroupsMode) {
      return;
    }

    this.switchAppsGroupsViewState(this.appsGroupsMode, appsGroupsFile);
    this.appsGroupsMode = appsGroupsFile;
    this.onAppsGroupsSelection();
  }

  /**
   * Puts the display choices made on an apps groups file aside and takes back the ones of the file being
   * displayed now, which start empty when it is shown for the first time. The three maps are swapped as a
   * whole: the panel is given them again by its inputs, and the renderer reads them back from here.
   */
  switchAppsGroupsViewState = (fromFile: string, toFile: string) => {
    if (fromFile != undefined) {
      let leftState = new AppsGroupsViewState();
      leftState.appGroupStates = this.appGroupStates;
      leftState.vmVisibilityStates = this.vmVisibilityStates;
      leftState.hiddenAppsGroups = this.hiddenAppsGroups;
      this.appsGroupsViewStatesByFile.set(fromFile, leftState);
    }

    let state = this.appsGroupsViewStatesByFile.get(toFile) ?? new AppsGroupsViewState();
    this.appsGroupsViewStatesByFile.delete(toFile);

    this.appGroupStates = state.appGroupStates;
    this.vmVisibilityStates = state.vmVisibilityStates;
    this.hiddenAppsGroups = state.hiddenAppsGroups;
  }

  // Called when the content of the displayed apps groups file was written back to the API
  onAppsGroupsSaved = (appsGroupsFile: string) => {
    // The apps groups of the displayed file changed, the view is rebuilt with them
    this.groupsApps = [];
    this.onAppsGroupsSelection();
  }

  /**
   * Called when the user changed a display choice in the floating panel (hidden apps group, folding mode,
   * hidden member). The panel wrote it in appGroupStates / vmVisibilityStates / hiddenAppsGroups already, the
   * view only has to be redrawn.
   */
  onAppsGroupsViewStateChanged = () => {
    this.reloadUi();
  }

  /**
   * Applies a display mode to the members of an apps group. A mode is only a preset over the visibility of the
   * members: it is applied once, and every member can then be folded or shown on its own.
   */
  applyAppGroupMode = (appGroup: AppsGroups, mode: AppsGroupMembersDisplayMode) => {
    let appGroupState = this.appGroupStates.get(appGroup?.name);
    if (appGroupState == undefined) {
      return;
    }
    // The members are already gathered from the apps groups this one includes
    applyDisplayMode(appGroupState, mode, appGroup.vms ?? [], appGroup.included_apps_groups ?? [],
                     this.vmVisibilityStates, this.memberRoles);
  }

  // Called from the cog of an app group of the graph: the panel is opened on that group
  showAppGroupSettings = (appGroup: FlowRessourceObject) => {
    this.appsGroupsPanelOpened = true;

    if (this.appsGroupsPanel != undefined) {
      this.appsGroupsPanel.focusOnGroup(appGroup.name);
    }
  }

  // Called from the cog of an apps group included in another one: the panel is opened on that group, in its file
  showIncludedAppsGroupSettings = (ref: { name: string, file: string }) => {
    this.onAppsGroupsFileChanged(ref.file);
    this.showAppGroupSettings({name: ref.name} as FlowRessourceObject);
  }

  /**
   * Opens or closes an apps group included in another one. The choice is kept with the display state of the
   * including group, created here if the user made no choice on it yet, as the panel would do.
   *
   * A box also opens on its own as soon as one of its members is shown: closing it therefore folds its
   * members, or it would open again straight away.
   */
  toggleIncludedAppsGroup = (inclusion: IncludedAppsGroupBoxState) => {
    if (!this.appGroupStates.has(inclusion.includingAppsGroupName)) {
      let appGroup = this.groupsApps.find((candidate) => candidate.name == inclusion.includingAppsGroupName);
      let appGroupState: AppGroupState = new AppGroupState();
      appGroupState.mode = appGroup?.mode ?? defaultDisplayMode(appGroup);
      appGroupState.appGroup = appGroup;
      appGroupState.uuid = appGroup?.uuid;
      appGroupState.name = inclusion.includingAppsGroupName;
      this.appGroupStates.set(inclusion.includingAppsGroupName, appGroupState);
    }

    toggleIncludedAppsGroupBox(this.appGroupStates.get(inclusion.includingAppsGroupName), this.vmVisibilityStates,
                               inclusion.key, inclusion);

    this.renderer.destroy(this);
    this.renderer.render(this);
  }

  // Called from the triangle of an apps group box
  toggleAppsGroupFolded = (appGroup: AppsGroups) => {
    toggleAppsGroupFolding(this, appGroup);

    this.renderer.destroy(this);
    this.renderer.render(this);
  }

  stopPropagation = (event) => {
    event.stopPropagation();
  }
}

/**
 * Key of an apps group included in another one, among the inclusions of that group: the same name may be
 * included from two different files.
 */
export function includedAppsGroupKey(inclusion: { name: string, file?: string }): string {
  return `${inclusion.file ?? ""}/${inclusion.name}`;
}

/**
 * Key of the visibility of a member in 'vmVisibilityStates': a member is shown or folded in one apps group, the
 * one drawing it, and not in every group that holds it too. A VM shown in a group would otherwise open the box
 * of every other group it belongs to as well.
 */
export function memberVisibilityKey(appGroupName: string, memberUuid: string): string {
  return `${appGroupName}\n${memberUuid}`;
}

/**
 * Applies a display mode to an apps group: a preset over the visibility of its members, over the boxes of the
 * apps groups it includes, and over its inside traffic. It is applied once, and each of these choices can then
 * be changed on its own.
 *
 *  - the included apps groups get a box at every level but 'summarized', closed until one of their members
 *    is shown, whatever was opened or closed by hand,
 *  - the flows inside the apps group are only drawn at the last level, 'all_traffic'.
 */
export function applyDisplayMode(state: AppGroupState, mode: AppsGroupMembersDisplayMode,
                                 members: { uuid: string }[], inclusions: { name: string, file?: string }[],
                                 vmVisibilityStates: Map<string, boolean>, memberRoles: Map<string, MemberRoles>) {
  for (let member of members) {
    vmVisibilityStates.set(memberVisibilityKey(state.name, member.uuid),
                           memberIsVisibleInMode(memberRoles.get(member.uuid), mode));
  }

  state.expandedIncludedAppsGroups.clear();
  state.hiddenIncludedAppsGroups = mode == "summarized"
    ? new Set(inclusions.map((inclusion) => includedAppsGroupKey(inclusion)))
    : new Set();

  state.showInternalFlows = mode == "all_traffic";
}

/**
 * Opens or closes the box of an included apps group. The box as last drawn tells whether it is opened; one that
 * was not drawn (its members exchanged nothing) only follows the choice of the user.
 *
 * Opening a box shows its members, as a folded member is drawn on its box and an opened box would hold nothing.
 * A box also opens on its own as soon as one of its members is shown: closing it therefore folds its members,
 * or it would open again straight away.
 */
/**
 * Whether an apps group is drawn folded into a single box: as the user left it, or as the view draws its groups
 * by default.
 */
export function isAppsGroupFolded(host: FlowViewHost, appGroupName: string): boolean {
  return host.appGroupStates.get(appGroupName)?.folded ?? host.appsGroupsFoldedByDefault ?? false;
}

/**
 * Members an apps group lists itself, or matches by its rules: the ones its included groups bring in are drawn
 * in the boxes of these groups.
 */
export function ownAppsGroupMembers(appGroup: AppsGroups): string[] {
  let includedUuids = new Set((appGroup.resolvedInclusions ?? [])
    .flatMap((inclusion) => inclusion.members.map((member) => member.uuid)));
  return (appGroup.vms ?? []).map((member) => member.uuid).filter((uuid) => !includedUuids.has(uuid));
}

/**
 * Folds an apps group into a single box, or unfolds it. As an included group does when it is opened, an unfolded
 * group shows its own members, and its included groups as closed boxes; folding it folds its members back.
 */
export function toggleAppsGroupFolding(host: FlowViewHost, appGroup: AppsGroups) {
  if (!host.appGroupStates.has(appGroup.name)) {
    let appGroupState: AppGroupState = new AppGroupState();
    appGroupState.mode = appGroup.mode ?? defaultDisplayMode(appGroup);
    appGroupState.appGroup = appGroup;
    appGroupState.uuid = appGroup.uuid;
    appGroupState.name = appGroup.name;
    host.appGroupStates.set(appGroup.name, appGroupState);
  }

  let state = host.appGroupStates.get(appGroup.name);
  state.folded = !isAppsGroupFolded(host, appGroup.name);
  for (let uuid of ownAppsGroupMembers(appGroup)) {
    host.vmVisibilityStates.set(memberVisibilityKey(appGroup.name, uuid), !state.folded);
  }
}

/**
 * Number of levels an apps group can be unfolded by: 1 for its own members, and one more per level of the groups
 * it includes, as far as they go.
 */
export function appsGroupNestingDepth(appGroup: AppsGroups): number {
  let depthOf = (inclusions: ResolvedAppsGroupsInclusion[]): number => {
    return Math.max(0, ...(inclusions ?? []).map((inclusion) => 1 + depthOf(inclusion.inclusions)));
  };
  return 1 + depthOf(appGroup.resolvedInclusions);
}

/**
 * Unfolds apps groups by a number of levels, as the user would by hand: none leaves them folded, one shows the
 * members of each group and the groups it includes as closed boxes, two opens these boxes as well, and so on.
 * Half a level opens the groups on the groups they include alone, as closed boxes, their own members staying
 * folded into them: a group including none stays folded, as it would be drawn as an empty box.
 * A group the user already folded or unfolded is left as it is unless 'all' is set, its display state being
 * then made anew.
 */
export function applyAppsGroupsNestingLevel(host: FlowViewHost, appsGroups: AppsGroups[], level: number,
                                            all: boolean) {
  // A group included by another one of the file is drawn inside it, not on its own (see computeVisualizationData):
  // showing its members as its own would open its box in the group including it
  let includedNames = new Set(appsGroups.flatMap((appGroup) => (appGroup.included_apps_groups ?? [])
    .filter((included) => (included.file ?? host.appsGroupsMode) == host.appsGroupsMode && included.name != appGroup.name)
    .map((included) => included.name)));

  for (let appGroup of appsGroups) {
    if (includedNames.has(appGroup.name) || host.hiddenAppsGroups.has(appGroup.name)) {
      continue;
    }
    if (!all && host.appGroupStates.has(appGroup.name)) {
      continue;
    }
    // Folded is what a group is drawn as by default
    if (!all && level == 0) {
      continue;
    }

    let state = new AppGroupState();
    state.mode = appGroup.mode ?? defaultDisplayMode(appGroup);
    state.appGroup = appGroup;
    state.uuid = appGroup.uuid;
    state.name = appGroup.name;
    // The inside traffic is not a matter of nesting: a group showing it keeps showing it
    state.showInternalFlows = host.appGroupStates.get(appGroup.name)?.showInternalFlows ?? false;
    state.folded = level == 0 || (level < 1 && (appGroup.resolvedInclusions ?? []).length == 0);
    host.appGroupStates.set(appGroup.name, state);

    for (let member of appGroup.vms ?? []) {
      host.vmVisibilityStates.delete(memberVisibilityKey(appGroup.name, member.uuid));
    }
    if (level < 1) {
      continue;
    }
    for (let uuid of ownAppsGroupMembers(appGroup)) {
      host.vmVisibilityStates.set(memberVisibilityKey(appGroup.name, uuid), true);
    }

    openIncludedAppsGroupsBoxes(host, state, appGroup.resolvedInclusions, undefined, level);
  }
}

/**
 * Opens the boxes of included apps groups down to a number of levels, as unfolding their apps group by that many
 * levels does: the boxes of the first level open as soon as two levels are asked for, and show their own
 * members, the ones of the groups they include in turn staying folded into their own boxes. 'parentKey' is the
 * key of the box holding these ones, none for the boxes drawn right in the apps group.
 */
function openIncludedAppsGroupsBoxes(host: FlowViewHost, state: AppGroupState,
                                     inclusions: ResolvedAppsGroupsInclusion[], parentKey: string, level: number,
                                     depth: number = 1) {
  for (let inclusion of inclusions ?? []) {
    if (depth >= level) {
      continue;
    }
    // The boxes are keyed as the renderer keys them (see registerIncludedBoxes)
    let key = parentKey == undefined
      ? includedAppsGroupKey(inclusion)
      : `${parentKey}>${includedAppsGroupKey(inclusion)}`;
    state.expandedIncludedAppsGroups.add(key);
    showOwnIncludedMembers(host, state, inclusion);
    openIncludedAppsGroupsBoxes(host, state, inclusion.inclusions, key, level, depth + 1);
  }
}

// Shows the members of an included apps group that none of the groups it includes in turn brought in
function showOwnIncludedMembers(host: FlowViewHost, state: AppGroupState, inclusion: ResolvedAppsGroupsInclusion) {
  let inChildren = new Set((inclusion.inclusions ?? [])
    .flatMap((child) => child.members.map((member) => member.uuid)));
  for (let member of inclusion.members) {
    if (!inChildren.has(member.uuid)) {
      host.vmVisibilityStates.set(memberVisibilityKey(state.name, member.uuid), true);
    }
  }
}

/**
 * Inclusion of an apps group a box stands for, found from the key of the box (see registerIncludedBoxes): the
 * keys of the boxes it is drawn in, from the outermost one, joined by '>'.
 */
export function findResolvedInclusion(appGroup: AppsGroups, key: string): ResolvedAppsGroupsInclusion {
  let inclusion: ResolvedAppsGroupsInclusion = undefined;
  let inclusions = appGroup?.resolvedInclusions ?? [];
  for (let part of key.split(">")) {
    inclusion = inclusions.find((candidate) => includedAppsGroupKey(candidate) == part);
    if (inclusion == undefined) {
      return undefined;
    }
    inclusions = inclusion.inclusions ?? [];
  }
  return inclusion;
}

/**
 * Number of levels the box of an included apps group can be unfolded by, as for an apps group (see
 * appsGroupNestingDepth): 1 for its own members, and one more per level of the groups it includes.
 */
export function includedAppsGroupNestingDepth(inclusion: ResolvedAppsGroupsInclusion): number {
  let depthOf = (inclusions: ResolvedAppsGroupsInclusion[]): number => {
    return Math.max(0, ...(inclusions ?? []).map((child) => 1 + depthOf(child.inclusions)));
  };
  return 1 + depthOf(inclusion.inclusions);
}

/**
 * Unfolds the box of an included apps group by a number of levels, whatever was opened or closed in it by hand,
 * as applyAppsGroupsNestingLevel does for a whole apps group: none closes it, half a level opens it on the
 * groups it includes as closed boxes, its own members staying folded into it, one shows these members as well,
 * two opens the boxes inside of it, and so on. 'memberUuids' are the resources the box was drawn with, the
 * networks it holds resolved into the resources they gathered, which are folded with the rest.
 */
export function applyIncludedAppsGroupNestingLevel(host: FlowViewHost, appGroup: AppsGroups, key: string,
                                                   level: number, memberUuids: string[] = []) {
  let inclusion = findResolvedInclusion(appGroup, key);
  if (inclusion == undefined) {
    return;
  }

  if (!host.appGroupStates.has(appGroup.name)) {
    let appGroupState = new AppGroupState();
    appGroupState.mode = appGroup.mode ?? defaultDisplayMode(appGroup);
    appGroupState.appGroup = appGroup;
    appGroupState.uuid = appGroup.uuid;
    appGroupState.name = appGroup.name;
    host.appGroupStates.set(appGroup.name, appGroupState);
  }
  let state = host.appGroupStates.get(appGroup.name);
  // The box is only drawn in the apps group unfolded
  state.folded = false;

  // Everything inside the box is folded first: a box opens on its own as soon as one of its members is shown
  state.expandedIncludedAppsGroups.delete(key);
  for (let nestedKey of Array.from(state.expandedIncludedAppsGroups)) {
    if (nestedKey.startsWith(`${key}>`)) {
      state.expandedIncludedAppsGroups.delete(nestedKey);
    }
  }
  for (let uuid of inclusion.members.map((member) => member.uuid).concat(memberUuids)) {
    host.vmVisibilityStates.delete(memberVisibilityKey(appGroup.name, uuid));
  }

  // A box including no group stays closed below one level, as an apps group does: it would be drawn empty
  if (level == 0 || (level < 1 && (inclusion.inclusions ?? []).length == 0)) {
    return;
  }
  state.expandedIncludedAppsGroups.add(key);
  if (level < 1) {
    return;
  }
  showOwnIncludedMembers(host, state, inclusion);
  openIncludedAppsGroupsBoxes(host, state, inclusion.inclusions, key, level);
}

export function toggleIncludedAppsGroupBox(state: AppGroupState, vmVisibilityStates: Map<string, boolean>,
                                           key: string, box: IncludedAppsGroupBoxState | undefined) {
  let expanded = box?.expanded ?? state.expandedIncludedAppsGroups.has(key);

  if (!expanded) {
    state.expandedIncludedAppsGroups.add(key);
    // An opened box shows its members: folded, they would leave it empty, their flows drawn on the box itself.
    // The members of the groups it includes in turn stay folded into their own boxes, closed, as an apps group
    // shows them when it is unfolded.
    let shownUuids = (box?.ownMemberUuids ?? box?.memberUuids ?? [])
      .concat(box?.ownDeclaredMemberUuids ?? box?.declaredMemberUuids ?? []);
    for (let uuid of shownUuids) {
      vmVisibilityStates.set(memberVisibilityKey(state.name, uuid), true);
    }
    return;
  }

  // Closing a box closes the boxes inside of it as well
  state.expandedIncludedAppsGroups.delete(key);
  for (let nestedKey of Array.from(state.expandedIncludedAppsGroups)) {
    if (nestedKey.startsWith(`${key}>`)) {
      state.expandedIncludedAppsGroups.delete(nestedKey);
    }
  }
  for (let uuid of (box?.memberUuids ?? []).concat(box?.declaredMemberUuids ?? [])) {
    vmVisibilityStates.set(memberVisibilityKey(state.name, uuid), false);
  }
}

